import { Badge } from "@/components/ui/badge";
import { ExternalLink, LibraryBig, UsersRound } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";

import { AsideSection } from "../misc/AsideSection";
import type { AuditResearchDirectoryEntry, Company } from "../types";

export const CompanyResearchDirectory = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const { data, total, isPending } = useGetList<AuditResearchDirectoryEntry>(
    "audit_research_directory",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
      filter: { company_id: record.id },
    },
  );
  if (isPending || !data?.length) return null;

  const auditors = data.filter((entry) => entry.record_kind === "auditor");
  const socialProfiles = data.filter((entry) =>
    ["company_social", "expert_social"].includes(entry.record_kind),
  );
  const experts = data.filter((entry) => entry.record_kind === "expert");
  const directoryFilter = encodeURIComponent(
    JSON.stringify({ company_id: record.id }),
  );

  return (
    <AsideSection
      title={translate("resources.audit_research_directory.company_section")}
    >
      {record.sro_membership_status === "ceased" && auditors.length ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          {translate(
            "resources.audit_research_directory.ceased_company_people_notice",
          )}
        </div>
      ) : null}

      {socialProfiles.length ? (
        <div>
          <div className="mb-1 flex items-center gap-1 font-medium">
            <LibraryBig className="h-4 w-4" />
            {translate("resources.audit_research_directory.company_social", {
              smart_count: socialProfiles.length,
            })}
          </div>
          <div className="flex flex-wrap gap-1">
            {socialProfiles.map((entry) => (
              <a
                key={entry.id}
                href={entry.contact_url || entry.source_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Badge variant="outline" className="hover:bg-muted">
                  <ExternalLink className="h-3.5 w-3.5" />
                  {entry.platform || entry.name}
                </Badge>
              </a>
            ))}
          </div>
        </div>
      ) : null}

      {auditors.length ? (
        <details>
          <summary className="flex cursor-pointer items-center gap-1 font-medium">
            <UsersRound className="h-4 w-4" />
            {translate("resources.audit_research_directory.company_auditors", {
              smart_count: auditors.length,
            })}
          </summary>
          <div className="mt-2 max-h-80 space-y-2 overflow-y-auto pr-1">
            {auditors.map((auditor) => (
              <div key={auditor.id} className="border-l-2 pl-2 text-sm">
                <a
                  href={auditor.contact_url || auditor.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:no-underline"
                >
                  {auditor.name}
                </a>
                <div className="text-xs text-muted-foreground">
                  {[auditor.certificate_number, auditor.employment_type]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {experts.map((expert) => (
        <a
          key={expert.id}
          href={expert.contact_url || expert.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-1 underline hover:no-underline"
        >
          <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{expert.name}</span>
        </a>
      ))}

      <Link
        to={`/audit_research_directory?filter=${directoryFilter}`}
        className="text-sm underline hover:no-underline"
      >
        {translate("resources.audit_research_directory.view_company_entries", {
          smart_count: total ?? data.length,
        })}
      </Link>
    </AsideSection>
  );
};
